import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../store';
import { Status } from '../../../common/constants';
import { IDateRange } from '../analytics.types';
import { getDeviceAnalyticsApi } from '../analyticsApi';
import { normalizeDeviceAnalyticsResponse } from '../analyticsUtils';

const GET_DEVICE_ANALYTICS = 'analytics/getDeviceAnalytics';

export interface IOrganizationWiseDeviceItem {
  orgId: string;
  orgName: string;
  totalDevices: number;
  onlineDevices: number;
  devicesAdded: number;
  deviceGrowth: {
    label: string;
    count: number;
  }[];
  deviceGrowthDateGroupType: string;
}

interface IDeviceAnalyticsState {
  status: string;
  error: string | null;
  totalDevices: number;
  onlineDevices: number;
  offlineDevices : number;
  organizationWiseDevices: IOrganizationWiseDeviceItem[];
}

const initialState: IDeviceAnalyticsState = {
  status: Status.IDLE,
  error: null,
  totalDevices: 0,
  onlineDevices: 0,
  offlineDevices: 0,
  organizationWiseDevices: [],
};

export const getDeviceAnalytics = createAsyncThunk(
  GET_DEVICE_ANALYTICS,
  async (dateRange: IDateRange) => {
    const deviceAnalyticsResponse = await getDeviceAnalyticsApi(dateRange);
    return normalizeDeviceAnalyticsResponse(deviceAnalyticsResponse);
  }
);

const deviceAnalyticsSlice = createSlice({
  name: 'deviceAnalytics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDeviceAnalytics.pending, (state) => {
        state.status = Status.PENDING;
        state.error = null;
      })
      .addCase(getDeviceAnalytics.fulfilled, (state, action) => {
        state.status = Status.SUCCESS;
        state.error = null;
        state.totalDevices = action.payload.totalDevices;
        state.onlineDevices = action.payload.onlineDevices;
        state.offlineDevices = action.payload.offlineDevices;
        state.organizationWiseDevices = action.payload.organizationWiseDevices;
      })
      .addCase(getDeviceAnalytics.rejected, (state, action) => {
        state.status = Status.FAILED;
        state.error =
          action.error.message || 'Failed to fetch device analytics.';
      });
  },
});

export const selector = (state: RootState) => state.deviceAnalytics;

export default deviceAnalyticsSlice.reducer;